import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import Icon from '../../../base/icons/components/Icon';
import { IconCloseLarge, IconDotsHorizontal, IconDownload, IconTrash } from '../../../base/icons/svg';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../base/ui/components/web/ContextMenuItemGroup';
import { downloadChatLogFile, hasChatLogMessages } from '../../../chat-log/functions';
import { showNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { clearChatForEveryone, toggleChat } from '../../actions.web';
import { isFileSharingEnabled } from '../../../file-sharing/functions.any';
import { ChatTabs } from '../../constants';
import { getFocusedTab, isChatDisabled } from '../../functions';
/**
 * Custom header of the {@code ChatDialog}.
 *
 * @returns {React$Element<any>}
 */
function ChatHeader({ actionsClassName, className, actionsMenuContainerClassName, actionsMenuButtonClassName, actionsMenuClassName, isCCTabEnabled, isPollsEnabled, onCancel }) {
    const dispatch = useDispatch();
    const store = useStore();
    const { t } = useTranslation();
    const _isChatDisabled = useSelector(isChatDisabled);
    const focusedTab = useSelector(getFocusedTab);
    const fileSharingTabEnabled = useSelector(isFileSharingEnabled);
    const hasMessages = useSelector((state) => hasChatLogMessages(state));
    const isModerator = useSelector((state) => isLocalParticipantModerator(state));
    const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
    const onClose = useCallback(() => {
        if (onCancel) {
            onCancel();
            return;
        }
        dispatch(toggleChat());
    }, [dispatch, onCancel]);
    const onKeyPressHandler = useCallback(e => {
        if (onClose && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            onClose();
        }
    }, [onClose]);
    const onActionsMenuKeyPress = useCallback(e => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            setIsActionsMenuOpen(open => !open);
        }
    }, []);
    const onDownloadChat = useCallback(() => {
        setIsActionsMenuOpen(false);
        dispatch(showNotification({
            titleKey: 'recording.downloadChatTitle',
            descriptionKey: 'recording.chatDownloadConfirm',
            customActionNameKey: ['dialog.confirm', 'dialog.cancel'],
            customActionHandler: [() => {
                    const hasDownloaded = downloadChatLogFile(store.getState());
                    dispatch(showNotification({
                        titleKey: 'chat.title',
                        descriptionKey: hasDownloaded ? 'recording.chatDownloaded' : 'recording.chatEmpty'
                    }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
                    return true;
                }, () => true]
        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
    }, [dispatch, store]);
    const onClearChat = useCallback(() => {
        setIsActionsMenuOpen(false);
        dispatch(showNotification({
            titleKey: 'chat.clearTitle',
            descriptionKey: 'chat.clearConfirm',
            customActionNameKey: ['dialog.confirm', 'dialog.cancel'],
            customActionHandler: [() => {
                    dispatch(clearChatForEveryone());
                    return true;
                }, () => true]
        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
    }, [dispatch]);
    const actions = useMemo(() => {
        const menuActions = [{
                accessibilityLabel: t('recording.downloadChat'),
                icon: IconDownload,
                id: 'chat-header-download-chat',
                onClick: onDownloadChat,
                text: t('recording.downloadChat')
            }];
        if (isModerator) {
            menuActions.push({
                accessibilityLabel: t('chat.clear'),
                icon: IconTrash,
                id: 'chat-header-clear-chat',
                onClick: onClearChat,
                text: t('chat.clear')
            });
        }
        return menuActions;
    }, [isModerator, onClearChat, onDownloadChat, t]);
    let title = 'chat.title';
    if (!_isChatDisabled && focusedTab === ChatTabs.CHAT) {
        title = 'chat.tabs.chat';
    }
    else if (isPollsEnabled && focusedTab === ChatTabs.POLLS) {
        title = 'chat.tabs.polls';
    }
    else if (isCCTabEnabled && focusedTab === ChatTabs.CLOSED_CAPTIONS) {
        title = 'chat.tabs.closedCaptions';
    }
    else if (fileSharingTabEnabled && focusedTab === ChatTabs.FILE_SHARING) {
        title = 'chat.tabs.fileSharing';
    }
    else {
        // If the focused tab is not enabled, don't render the header.
        // This should not happen in normal circumstances since Chat.tsx already checks
        // if any tabs are available before rendering.
        return null;
    }
    return (React.createElement("div", { className: className || 'chat-dialog-header' },
        React.createElement("span", { "aria-level": 1, role: 'heading' }, t(title)),
        React.createElement("div", { className: actionsClassName },
            hasMessages && (React.createElement("div", { className: actionsMenuContainerClassName },
                React.createElement(Icon, { ariaLabel: t('toolbar.moreActions'), className: actionsMenuButtonClassName, onClick: () => setIsActionsMenuOpen(open => !open), onKeyPress: onActionsMenuKeyPress, role: 'button', src: IconDotsHorizontal, tabIndex: 0 }),
                React.createElement(ContextMenu, { activateFocusTrap: true, className: actionsMenuClassName, hidden: !isActionsMenuOpen, isDrawerOpen: isActionsMenuOpen, onDrawerClose: () => setIsActionsMenuOpen(false), onMouseLeave: () => setIsActionsMenuOpen(false) },
                    React.createElement(ContextMenuItemGroup, { actions: actions })))),
            React.createElement(Icon, { ariaLabel: t('toolbar.closeChat'), onClick: onClose, onKeyPress: onKeyPressHandler, role: 'button', src: IconCloseLarge, tabIndex: 0 }))));
}
export default ChatHeader;
